import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
} from '@angular/core';
import {skipWhilePreviousCall} from 'app/core/helpers/decorators';
import {setStyle, transitionEnd} from 'app/core/helpers/dom';
import {Coordinates2D, PositionDirection} from 'app/core/interfaces/overlay.interface';
import {DomService} from 'app/core/services/dom.service';
import {OverlayService} from 'app/core/services/overlay.service';
import {CustomFormComponent} from '../../classes/custom-form-component';
import {OverlayElementEditor} from '../../classes/overlay-element-editor';
import {DropDownContentComponent} from './drop-down-content/drop-down-content.component';

export const COLLAPSED_WIDTH_PROPERTY = '--width-collapsed';
export const COLLAPSED_HEIGHT_PROPERTY = '--height-collapsed';
export const EXPANDED_WIDTH_PROPERTY = '--width-expanded';
export const EXPANDED_HEIGHT_PROPERTY = '--height-expanded';

export enum ExpandingMode {
    inside = 'inside',
    outside = 'outside',
}

@Component({
    selector: 'drop-down',
    templateUrl: './drop-down.component.html',
    styleUrls: ['./drop-down.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropDownComponent extends CustomFormComponent implements OnInit, OnDestroy {
    @Input() selectedTemplate: TemplateRef<any>;
    @Input() optionsTemplate: TemplateRef<any>;
    @Input() manualToggle: boolean;
    @Input() dropIndicator: boolean;
    @Input() expandingMode: ExpandingMode;
    @Input() modern: boolean;
    @Input() mobileCenter = false;
    @Output() optionsVisibility = new EventEmitter<boolean>();
    @Output() contentRender = new EventEmitter<OverlayElementEditor>();
    @Output() contentHide = new EventEmitter<OverlayElementEditor>();
    optionsVisible = false;
    private contentId: string;
    private contentElement: HTMLElement;
    private removeDocClickListener: () => void;

    readonly mobileWidth = 768;

    constructor(
        cd: ChangeDetectorRef,
        $el: ElementRef<HTMLElement>,
        private domService: DomService,
        private overlayService: OverlayService
    ) {
        super($el, cd);
    }

    static getContentElement(editor: OverlayElementEditor, selector: string = 'drop-down-content'): HTMLElement {
        return editor.getElement().querySelector(selector);
    }

    static setCollapsedSize(el: HTMLElement, width: number, height: number) {
        setStyle(el, {
            [COLLAPSED_HEIGHT_PROPERTY]: `${height}px`,
            [COLLAPSED_WIDTH_PROPERTY]: `${width}px`,
        });
    }

    static setExpandedSize(el: HTMLElement, width: number, height: number) {
        setStyle(el, {
            [EXPANDED_HEIGHT_PROPERTY]: `${height}px`,
            [EXPANDED_WIDTH_PROPERTY]: `${width}px`,
        });
    }

    static hasCollapsedSize(el: HTMLElement): boolean {
        return [COLLAPSED_WIDTH_PROPERTY, COLLAPSED_HEIGHT_PROPERTY].every(prop => isFinite(parseInt(el.style.getPropertyValue(prop), 10)));
    }

    static hasExpandedSize(el: HTMLElement): boolean {
        return [EXPANDED_WIDTH_PROPERTY, EXPANDED_HEIGHT_PROPERTY].every(prop => isFinite(parseInt(el.style.getPropertyValue(prop), 10)));
    }

    static getContentPosition(collapsedRect: DOMRect, expandedRect: DOMRect): {coordinates: Coordinates2D; direction: PositionDirection} {
        let coordinates = {x: collapsedRect.left, y: collapsedRect.top};
        let direction = {
            vertical: 'down',
            horizontal: 'right',
        };
        if (coordinates.y + expandedRect.height > window.innerHeight) {
            direction.vertical = 'up';
            coordinates.y = collapsedRect.bottom;
        }
        if (expandedRect.width >= window.innerWidth - 1) {
            direction.horizontal = 'right';
            coordinates.x = 0;
        } else if (coordinates.x + expandedRect.width > window.innerWidth) {
            direction.horizontal = 'left';
            coordinates.x = collapsedRect.right;
        }
        return {
            coordinates,
            direction,
        };
    }

    ngOnInit() {
        this.initInputValue('disabled', false);
        this.initInputValue('manualToggle', false);
        this.initInputValue('dropIndicator', true);
        this.initInputValue('expandingMode', ExpandingMode.inside);
    }

    ngOnDestroy() {
        this.safeRemoveDocClickListener();
        if (this.optionsVisible && this.contentElement) {
            this.overlayService.hideTemplate(this.contentId).then(editor => editor.removeElement());
        }
    }

    @skipWhilePreviousCall()
    async toggleContent(value?: boolean) {
        this.optionsVisible = value === undefined ? !this.optionsVisible : value;
        if (this.optionsVisible) {
            await this.openDropDownContent();
            this.removeDocClickListener = this.domService.onDocClick(e => this.onDocClick(e));
        } else {
            this.safeRemoveDocClickListener();
            await this.closeDropDownContent();
        }
        this.optionsVisibility.emit(this.optionsVisible);
    }

    onDocClick(event: Event) {
        if (!this.contentElement.contains(event.target as Node)) {
            this.toggleContent(false);
            this.cd.markForCheck();
        }
    }

    onClick(e: MouseEvent) {
        if (this.manualToggle) {
            return;
        }
        return this.onToggleBtnClick(e);
    }

    onToggleBtnClick(event: MouseEvent) {
        event.stopPropagation();
        if (!this.disabled) {
            this.toggleContent();
        }
    }

    /* istanbul ignore next */
    updateHeight() {
        if (this.contentElement) {
            let wrapper = this.contentElement.querySelector('.drop-down-content-wrapper') as HTMLElement;
            let borderWidth = parseInt(getComputedStyle(this.contentElement).getPropertyValue('--border-width'), 10);

            let copyContainer = document.createElement('div');
            let copy = wrapper.cloneNode(true) as HTMLDivElement;

            copyContainer.style.cssText = 'visibility: hidden; position: fixed;';
            copy.style.setProperty('height', 'auto');

            copyContainer.append(copy);
            wrapper.parentNode.append(copyContainer);
            requestAnimationFrame(() => {
                wrapper.parentElement.style.setProperty('--height-expanded', `${copy.getBoundingClientRect().height + 2 * borderWidth}px`);
                copyContainer.remove();
                this.cd.markForCheck();
            });
        }
    }

    private safeRemoveDocClickListener() {
        if (typeof this.removeDocClickListener === 'function') {
            this.removeDocClickListener();
            this.removeDocClickListener = null;
        }
    }

    private async openDropDownContent(): Promise<void> {
        let editor = await this.overlayService.renderTemplate(DropDownContentComponent, {
            template: this.optionsTemplate,
        });
        this.contentId = editor.id;
        this.contentElement = editor.getElement();
        editor.copyCustomProperties(this.$el.nativeElement);
        editor.copyStyles(this.$el.nativeElement, ['font-size']);
        await editor.tick();
        this.contentRender.emit(editor);
        let dropDownContent = DropDownComponent.getContentElement(editor);
        let {collapsedRect, expandedRect} = this.getRects(editor);
        if (!DropDownComponent.hasCollapsedSize(dropDownContent)) {
            let {width, height} = this.isOutsideMode() ? {width: 0, height: 0} : collapsedRect;
            DropDownComponent.setCollapsedSize(dropDownContent, width, height);
        }
        if (!DropDownComponent.hasExpandedSize(dropDownContent)) {
            DropDownComponent.setExpandedSize(dropDownContent, expandedRect.width, expandedRect.height);
        }
        await editor.tick();
        if (!editor.positionSpecified) {
            if (this.isMobile()) {
                editor.setPosition(
                    {
                        x: Math.max((window.innerWidth - expandedRect.width) / 2, 0),
                        y: Math.max((window.innerHeight - expandedRect.height) / 2, 0),
                    },
                    {
                        vertical: 'down',
                        horizontal: 'right',
                    }
                );
            } else {
                let {coordinates, direction} = DropDownComponent.getContentPosition(collapsedRect, expandedRect);
                if (this.isOutsideMode()) {
                    coordinates.y += collapsedRect.height + 5;
                }
                editor.setPosition(coordinates, direction);
            }
        }
        let fixedParent = editor.hasFixedParent(this.$el.nativeElement);
        if (this.isOutsideMode()) {
            dropDownContent.style.setProperty('transition', 'none');
            dropDownContent.classList.add('outside');
        }
        await editor.tick();
        dropDownContent.style.removeProperty('transition');
        editor.showElement(!!fixedParent, fixedParent ? getComputedStyle(fixedParent).zIndex : null);
        await editor.tick();
        dropDownContent.classList.add('expanding');
        if (this.isMobile()) {
            await transitionEnd(dropDownContent, ['height']);
        } else {
            await transitionEnd(dropDownContent, ['width', 'height']);
        }

        dropDownContent.classList.add('expanded');
    }

    private async closeDropDownContent(): Promise<void> {
        let editor = await this.overlayService.hideTemplate(this.contentId);
        this.contentHide.emit(editor);
        if (editor.getElement()) {
            let dropDownContent = DropDownComponent.getContentElement(editor);
            let {collapsedRect, expandedRect} = this.getRects(editor);
            if (!DropDownComponent.hasCollapsedSize(dropDownContent)) {
                DropDownComponent.setCollapsedSize(
                    dropDownContent,
                    this.isMobile() ? expandedRect.width : collapsedRect.width,
                    collapsedRect.height
                );

                await editor.tick();
            }
            dropDownContent.classList.remove('expanded');
            await editor.tick();
            dropDownContent.classList.remove('expanding');

            if (!this.isMobile()) {
                await transitionEnd(dropDownContent, ['width', 'height']);
            }
            editor.removeElement();
        }
        this.contentElement = null;
    }

    private getRects(editor: OverlayElementEditor) {
        let dropDownContent = editor.getElement().firstElementChild as HTMLElement;
        let collapsedRect = this.$el.nativeElement.getBoundingClientRect() as DOMRect;
        let expandedRect = dropDownContent.getBoundingClientRect() as DOMRect;
        return {
            collapsedRect,
            expandedRect,
        };
    }

    isMobile() {
        return this.domService.isMobile() && this.mobileCenter && window.innerWidth < this.mobileWidth;
    }

    isInsideMode(): boolean {
        return this.expandingMode === ExpandingMode.inside;
    }

    isOutsideMode(): boolean {
        return this.expandingMode === ExpandingMode.outside;
    }
}
